/**************************************************
 * 
 * Global QMod Component Changes
 * 
 * These are temporary hotfixes until QMod makes official updates.
 * 
 **************************************************/

/**
 * Function to delay execution by a given time in milliseconds.
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Centralized function to apply all QMod accessibility fixes.
 * Called on initial load and after browser resize.
 * 
 * @since CT-784
 */
async function applyAllQmodAccessibilityFixes() {
    jq('[id^=highcharts-].highcharts-container').attr("role", "region");
    assignUniqueAriaLabels();
    assignUniqueFilterIds();
    jq('[id^=highcharts-end-of-chart-marker-]').attr("role", "region");
    
    await Promise.all([
        assignUniqueAttributes(),
        fixEmptyTableHeaders(),
        addTabIndexToVolumeToggle(),
        fixInvalidAriaAttributes(),
        assignUniqueLabels()
    ]);
    
    setAriaLabelsForIcons();
    fixDateRangeButtonLabels();
    linkQmodChartToolbarToChart();
}

/**
 * Debounce utility to prevent excessive function calls during resize.
 * 
 * @since CT-784
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Reapply QMod accessibility fixes on browser resize.
 * Uses debounce to wait until resize is complete.
 * 
 * @since CT-784
 */
const reapplyQmodFixesOnResize = debounce(function() {
    applyAllQmodAccessibilityFixes();
}, 250);

window.addEventListener('resize', reapplyQmodFixesOnResize);

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique aria-labels to highcharts containers.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAriaLabels() {
    const charts = jq('[id^=highcharts-].highcharts-container');
    charts.each(function (index) {
        const uniqueLabel = `Chart Container ${index + 1}`;
        jq(this).attr("aria-label", uniqueLabel);
    });
}

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique IDs to duplicate filters within SVG defs.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueFilterIds() {
    const filters = jq('svg defs filter[id="highcharts-drop-shadow-0"]');
    filters.each(function (index) {
        if (index > 0) {
            const newId = `highcharts-drop-shadow-${Math.random().toString(36).substr(2, 9)}`;
            jq(this).attr('id', newId);

            // Update any references to this filter id in the SVG
            const references = jq(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
            references.each(function () {
                jq(this).attr('filter', `url(#${newId})`);
            });
        }
    });
}

// Call toggleTabIndex and apply all accessibility fixes once QMod content is ready
observeForContent('.highcharts-container').then(() => {
    toggleTabIndex(null, true);
    applyAllQmodAccessibilityFixes();
    watchQmodChartCreditsLink();
    watchQmodChartToolbar();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Fixes the unique ID and missing aria attributes.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAttributes() {
    return new Promise((resolve) => {
        function generateUniqueId(baseId) {
            return baseId + '-' + Math.random().toString(36).substr(2, 9);
        }

        const ids = [
            'highcharts-end-of-chart-marker-0',
            'highcharts-screen-reader-region-before-0',
            'highcharts-drop-shadow-0',
            'highcharts-screen-reader-region-after-0',
            'highcharts-2ih7fo0-0',
            'highcharts-fjy84ra-0'
        ];

        ids.forEach((baseId) => {
            const elements = document.querySelectorAll(`#${baseId}`);
            elements.forEach((element, index) => {
                if (index > 0) {
                    const newId = generateUniqueId(baseId);
                    element.setAttribute('id', newId);
                }
            });
        });

        const landmarkElements = document.querySelectorAll('.highcharts-exit-anchor, .highcharts-container');
        landmarkElements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        const filters = document.querySelectorAll('svg defs filter[id="highcharts-drop-shadow-0"]');
        filters.forEach((filter, index) => {
            if (index > 0) {
                const newId = generateUniqueId('highcharts-drop-shadow-0');
                filter.setAttribute('id', newId);

                const references = document.querySelectorAll(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
                references.forEach((reference) => {
                    jq(reference).attr('filter', `url(#${newId})`);
                });
            }
        });

        const specificElements = ['highcharts-3yiiuel-0', 'highcharts-pu44erb-0'];
        specificElements.forEach((baseId, index) => {
            const element = document.querySelector(`#${baseId}`);
            if (element && !element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart Specific ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueAttributes();
});

/**
 * Page Type: QMod - Historical Data
 * 
 * Creates and inserts a label for an input selected by a specific CSS selector.
 * 
 * @since QMod v1.69.0
 */
function createLabelForInputBySelector(selector, labelText) {
    let inputElement = document.querySelector(selector);
    if (!inputElement) {
        return;
    }

    if (!inputElement.id) {
        inputElement.id = 'input-' + Math.random().toString(36).substr(2, 9);
    }

    let label = document.querySelector('label[for="' + CSS.escape(inputElement.id) + '"]');
    if (!label) {
        label = document.createElement('label');
        label.htmlFor = inputElement.id;
        label.classList.add('visually-hidden');
        inputElement.parentNode.insertBefore(label, inputElement);
    }

    label.classList.add('visually-hidden');
    if (label.textContent !== labelText) {
        label.textContent = labelText;
    }
}

/**
 * Page Type: QMod - Historical Data
 *
 * The from/to calendar buttons render with a generic "Open datepicker" name.
 * QuoteMedia can overwrite the template aria-label, so set "Date from" and
 * "Date to" again after each render, and keep a visually hidden label on each
 * date input as a backup.
 */
function setHistoricalDatePickerButtonLabels() {
    if (!document.body.classList.contains('page-type-qmod---historical-data')) {
        return;
    }

    const pickers = [
        { root: '#qmod-fromdate', label: 'Date from' },
        { root: '#qmod-todate', label: 'Date to' }
    ];

    pickers.forEach((picker) => {
        const root = document.querySelector(picker.root);
        const control = root ? root.closest('.qmod-calendar-control') : null;
        const button = control ? control.querySelector('.inner-addon > button.duet-date__toggle') : null;
        if (button && button.getAttribute('aria-label') !== picker.label) {
            button.setAttribute('aria-label', picker.label);
        }

        createLabelForInputBySelector(picker.root + ' input.duet-date__input', picker.label);
    });
}

function watchHistoricalDatePickerLabels() {
    if (!document.body.classList.contains('page-type-qmod---historical-data') || window.__qmodHistoricalDateLabelObserver) {
        return;
    }

    setHistoricalDatePickerButtonLabels();

    const root = document.querySelector('.historical-data') || document.body;
    const observer = new MutationObserver(() => {
        setHistoricalDatePickerButtonLabels();
    });
    observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-label']
    });

    window.__qmodHistoricalDateLabelObserver = observer;
}

if (jq('body').hasClass('page-type-qmod---historical-data')) {
    observeForContent('#qmod-fromdate').then(() => {
        watchHistoricalDatePickerLabels();
    });
}

/**
 * Page Type: QMod - Historical Data
 * 
 * Adds a span element with class "visually-hidden" to all matching th elements.
 * 
 * @since QMod v1.69.0
 */
function addSrOnlySpanToThElements() {
    // Select all th QMod elements with the specified class
    let thElements = document.querySelectorAll('th.qmod-control.control.sorting_disabled.dtr-hidden');

    // Iterate over each th element
    thElements.forEach(function(th) {
        // Check if there's already an visually-hidden span, if not, add one
        if (!th.querySelector('.visually-hidden')) {
            let span = document.createElement('span');
            span.className = 'visually-hidden';
            span.textContent = 'Control Header';
            span.setAttribute('aria-hidden', 'true');
    
            th.appendChild(span);
        }
    });
}

// Call the SR Only Function after delay
delay(2000).then(() => {
    addSrOnlySpanToThElements();
});

/**
 * Observes for content and resolves the promise once content is found.
 */
function observeForContent(selector) {
    return new Promise((resolve, reject) => {
        let checkInterval = setInterval(() => {
            if (jq(selector).html()) {
                clearInterval(checkInterval);
                resolve();
            }
        }, 100); // Check every 100 milliseconds
    });
}

/**
 * Shared by QMod tables whose column headers sort the table.
 *
 * Dividends puts a button inside each sortable `<th>`, and that button is the
 * control. Historical Data does not: the `<th>` itself is the control, so it
 * needs a tabindex, an accessible name, and aria-sort.
 *
 * @param {HTMLTableElement} table
 */
function fixQmodSortHeadersInTable(table) {
    if (!table) {
        return;
    }

    let caption = table.querySelector('caption');
    if (!caption) {
        caption = document.createElement('caption');
        caption.className = 'visually-hidden';
        table.insertBefore(caption, table.firstChild);
    }
    if (!caption.classList.contains('visually-hidden')) {
        caption.classList.add('visually-hidden');
    }
    if (caption.textContent !== 'Column headers are sortable') {
        caption.textContent = 'Column headers are sortable';
    }

    table.querySelectorAll('thead th.sorting').forEach((th) => {
        const button = th.querySelector('button');
        const name = (button || th).textContent.replace(/\s+/g, ' ').trim();
        if (!name) {
            return;
        }

        let sortValue = 'none';
        if (th.classList.contains('sorting_asc')) {
            sortValue = 'ascending';
        } else if (th.classList.contains('sorting_desc')) {
            sortValue = 'descending';
        }
        if (th.getAttribute('aria-sort') !== sortValue) {
            th.setAttribute('aria-sort', sortValue);
        }

        // Dividends: the inner button is already the named sort control.
        if (button) {
            return;
        }

        const label = 'Sort by ' + name + ', sortable';
        if (th.getAttribute('tabindex') !== '0') {
            th.setAttribute('tabindex', '0');
        }
        if (th.getAttribute('aria-label') !== label) {
            th.setAttribute('aria-label', label);
        }
    });
}

/**
 * Page Type: QMod - Historical Data
 *
 * Names the price-history sort headers. QuoteMedia redraws the table on sort
 * and date changes, so this is safe to call repeatedly.
 */
function fixHistoricalSortHeaders() {
    if (!document.body.classList.contains('page-type-qmod---historical-data')) {
        return;
    }

    document.querySelectorAll('#qmod-price-history table').forEach((table) => {
        fixQmodSortHeadersInTable(table);
    });
}

/**
 * Page Type: QMod - Historical Data
 *
 * Re-apply sort-header names after QuoteMedia rebuilds the table.
 */
function watchHistoricalSortHeaders() {
    if (!document.body.classList.contains('page-type-qmod---historical-data') || window.__qmodHistoricalSortObserver) {
        return;
    }

    fixHistoricalSortHeaders();

    const root = document.querySelector('.historical-data') || document.body;
    const observer = new MutationObserver(() => {
        fixHistoricalSortHeaders();
    });
    observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'aria-sort', 'aria-label']
    });

    window.__qmodHistoricalSortObserver = observer;
}

if (jq('body').hasClass('page-type-qmod---historical-data')) {
    observeForContent('#qmod-price-history table').then(() => {
        watchHistoricalSortHeaders();
    });
}

/**
 * Page Type: QMod - Dividends
 *
 * DataTables focuses each sortable th, and the template also puts a button
 * inside it. That is two tab stops: the th has no role, and the button does
 * not sort or name the action. The button is the one stop. The th keeps
 * aria-sort and stays out of the tab order.
 */
function getDividendsSortHeaderState(th) {
    if (th.classList.contains('sorting_asc')) {
        return {
            ariaSort: 'ascending',
            labelSuffix: ', sorted ascending'
        };
    }
    if (th.classList.contains('sorting_desc')) {
        return {
            ariaSort: 'descending',
            labelSuffix: ', sorted descending'
        };
    }
    return {
        ariaSort: 'none',
        labelSuffix: ', sortable'
    };
}

function fixDividendsSortHeadersInTable(table) {
    if (!table) {
        return;
    }

    let caption = table.querySelector('caption');
    if (!caption) {
        caption = document.createElement('caption');
        caption.className = 'visually-hidden';
        table.insertBefore(caption, table.firstChild);
    }
    if (!caption.classList.contains('visually-hidden')) {
        caption.classList.add('visually-hidden');
    }
    if (caption.textContent !== 'Column headers are sortable') {
        caption.textContent = 'Column headers are sortable';
    }

    table.querySelectorAll('thead th.sorting, thead th.sorting_asc, thead th.sorting_desc').forEach((th) => {
        const button = th.querySelector('button');
        if (!button) {
            return;
        }

        const columnName = button.textContent.replace(/\s+/g, ' ').trim();
        if (!columnName) {
            return;
        }

        const state = getDividendsSortHeaderState(th);
        const sortLabel = 'Sort by ' + columnName + state.labelSuffix;

        if (th.getAttribute('aria-sort') !== state.ariaSort) {
            th.setAttribute('aria-sort', state.ariaSort);
        }
        if (th.hasAttribute('aria-label')) {
            th.removeAttribute('aria-label');
        }
        if (th.getAttribute('tabindex') !== '-1') {
            th.setAttribute('tabindex', '-1');
        }
        if (button.getAttribute('type') !== 'button') {
            button.setAttribute('type', 'button');
        }
        if (button.getAttribute('aria-label') !== sortLabel) {
            button.setAttribute('aria-label', sortLabel);
        }
        if (button.dataset.qmodSortBound === 'true') {
            return;
        }

        button.dataset.qmodSortBound = 'true';
        // DataTables also sorts on Enter keypress at the header. The button's
        // own click already sorts, so a bubbling Enter keypress sorts again.
        button.addEventListener('keydown', (evt) => {
            if (evt.key !== 'Enter' && evt.key !== ' ') {
                return;
            }
            evt.stopPropagation();
        });
        button.addEventListener('keypress', (evt) => {
            if (evt.key !== 'Enter' && evt.key !== ' ') {
                return;
            }
            evt.stopPropagation();
        });
        button.addEventListener('click', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            th.click();
        });
    });
}

function fixDividendsSortHeaders() {
    if (!document.body.classList.contains('page-type-qmod---dividends')) {
        return;
    }

    document.querySelectorAll('.dividends-data table.qmod-table').forEach((table) => {
        fixDividendsSortHeadersInTable(table);
    });
}

const debouncedFixDividendsSortHeaders = debounce(fixDividendsSortHeaders, 100);

function watchDividendsSortHeaders() {
    if (
        !document.body.classList.contains('page-type-qmod---dividends')
        || window.__qmodDividendsSortObserver
    ) {
        return;
    }

    fixDividendsSortHeaders();

    const root = document.querySelector('.dividends-data') || document.body;
    const observer = new MutationObserver(() => {
        debouncedFixDividendsSortHeaders();
    });
    observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'aria-sort', 'aria-label', 'tabindex']
    });

    window.__qmodDividendsSortObserver = observer;
}

if (jq('body').hasClass('page-type-qmod---dividends')) {
    observeForContent('.dividends-data th.sorting').then(() => {
        watchDividendsSortHeaders();
    });
}

/**
 * Global for all QMod Components with Legends
 * 
 * Sets tabindex and accessible click for .qmod-legend li elements.
 */
delay(1000).then(() => {
    jq('.qmod-legend li').each(function(i) {
        jq(this).attr('tabindex', 0);
        jq(this).accessibleClick(function(evt) {
            // We want to use the accessibleClick method so that the logic surrounding
            // keys is consistent; However, avoid recursion by causing an infinite click loop
            if (evt.type != 'click') {
                jq(this).click();
                return false;
            }
        });
    });
});

/**
 * Global for all QMod Components with Main Chart
 * 
 * Fixes the unique label for qmod-main-chart.
 */
function assignUniqueLabels() {
    return new Promise((resolve) => {
        const elements = document.querySelectorAll('.qmod-main-chart');

        elements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueLabels();
});

/**
 * Page Type: QMod - Dividends
 * 
 * Fixes invalid ARIA attribute names.
 * 
 * @since QMod v1.69.0
 */
function convertAriaRoleAttributes(root) {
    if (!root || root.nodeType !== 1) {
        root = document;
    }

    const targets = [];
    if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute('aria-role')) {
        targets.push(root);
    }
    if (root.querySelectorAll) {
        root.querySelectorAll('[aria-role]').forEach(el => targets.push(el));
    }

    targets.forEach(element => {
        const ariaRoleValue = element.getAttribute('aria-role');
        element.removeAttribute('aria-role');
        if (!element.getAttribute('role')) {
            element.setAttribute('role', ariaRoleValue);
        }
    });
}

function fixInvalidAriaAttributes() {
    return new Promise((resolve) => {
        convertAriaRoleAttributes(document);
        resolve();
    });
}

/**
 * QMOD/DataTables build and redraw their widgets (e.g. the Dividends pagination)
 * asynchronously and re-add the invalid `aria-role` attribute each time. A single
 * pass on DOMContentLoaded races the widget build, which lets axe intermittently
 * flag `aria-valid-attr` on the pagination buttons (.current, a[data-dt-idx],
 * #DataTables_Table_0_next). Keep a persistent observer that strips invalid
 * `aria-role` the moment it appears, on initial render and on every redraw.
 */
function watchInvalidAriaAttributes() {
    if (window.__qmodAriaRoleObserver) {
        return;
    }

    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.type === 'attributes') {
                if (mutation.target.hasAttribute('aria-role')) {
                    convertAriaRoleAttributes(mutation.target);
                }
            } else if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(node => convertAriaRoleAttributes(node));
            }
        });
    });

    observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-role']
    });

    window.__qmodAriaRoleObserver = observer;
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    fixInvalidAriaAttributes();
    watchInvalidAriaAttributes();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Toggles the tabindex and aria-hidden attributes of elements in the tab panels 
 * to prevent tabbing into hidden fields.
 * 
 * @since QMod v1.69.0
 */
function toggleTabIndex(event, isFirstLoad = false) {
    let next, previous;
    if (!isFirstLoad) {
        next = jq(event.target.hash);
        previous = jq(event.relatedTarget.hash);
    } else {
        next = jq('#tab_quote');
        previous = jq('#tab_historical');
    }

    // Add aria-hidden="true" and tabindex="-1" to the hidden tab-panel
    previous.attr('aria-hidden', 'true');
    previous.find('input, button:not(.no-tab), th.sorting, .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '-1');
    previous.find('.highcharts-exit-anchor').attr('aria-hidden', 'true');

    // Remove aria-hidden and set tabindex="0" from the active tab-panel
    next.removeAttr('aria-hidden');
    next.find('input, button:not(.no-tab), th.sorting, .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '0');
    next.find('.highcharts-exit-anchor').attr('aria-hidden', 'false');
}

// Update table inputs on tab click
jq('.stock-tab a[data-bs-toggle="tab"]').on('shown.bs.tab', function (event) {
    toggleTabIndex(event, false);
});

// Initial load adjustments
delay(2000).then(() => {
    toggleTabIndex(null, true);
});

/**
 * Ensure dropdown in historical data pane opens on Enter key press
 */
jq(document).on('keydown', '.qmod-a11y-dropdown2', function (event) {
    if (event.key === 'Enter') {
        let expanded = jq(this).attr('aria-expanded') === 'true';
        jq(this).attr('aria-expanded', !expanded);
        jq(this).click();
    }
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Ensures that down and up arrow icons are not hidden and have the appropriate aria-labels.
 */
function setAriaLabelsForIcons() {
    const downArrowIcon = jq('.qmod-change.qmod-ch-down .qmod-change-icon.fa.fa-arrow-circle-down');
    const upArrowIcon = jq('.qmod-change.qmod-ch-up .qmod-change-icon.fa.fa-arrow-circle-up');

    if (downArrowIcon.length > 0) {
        downArrowIcon.attr('aria-hidden', 'false');
        downArrowIcon.attr('aria-label', 'Down');
        downArrowIcon.attr('role', 'img');
        downArrowIcon.removeAttr('aria-disabled');
    }

    if (upArrowIcon.length > 0) {
        upArrowIcon.attr('aria-hidden', 'false');
        upArrowIcon.attr('aria-label', 'Up');
        upArrowIcon.attr('role', 'img');
        upArrowIcon.removeAttr('aria-disabled');
    }
}

// Call the function with a delay after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        setAriaLabelsForIcons();
    }, 2000); // 2-second delay
});

/**
 * Component: Zero-nav stock ticker
 *
 * The header ticker renders an empty `<div class="arrow">` whose direction is
 * only communicated by color (green up / red down) via the sibling `.change`
 * span's `qmod-ch-up` / `qmod-ch-down` class that QuoteMedia applies at runtime.
 * That is a WCAG 1.1.1 (non-text content) issue. Give the arrow `role="img"`
 * and a direction-aware `aria-label` so screen readers announce whether the
 * stock is up or down, keeping the label in sync as QuoteMedia re-renders.
 */
function setZeroNavStockArrowLabel() {
    document.querySelectorAll('.zero-nav-quote-box .arrow').forEach((arrow) => {
        const change = arrow.parentNode ? arrow.parentNode.querySelector('.change') : null;
        if (!change) {
            return;
        }

        let label = 'Stock price unchanged';
        if (change.classList.contains('qmod-ch-up')) {
            label = 'Stock price up';
        } else if (change.classList.contains('qmod-ch-down')) {
            label = 'Stock price down';
        }

        arrow.setAttribute('role', 'img');
        arrow.setAttribute('aria-label', label);
    });
}

/**
 * Component: Zero-nav stock ticker
 *
 * QuoteMedia builds and updates the ticker asynchronously, so watch each quote
 * box and re-apply the arrow label on every render/redraw.
 */
function watchZeroNavStockArrow() {
    const boxes = document.querySelectorAll('.zero-nav-quote-box');
    if (!boxes.length || window.__zeroNavArrowObserver) {
        return;
    }

    setZeroNavStockArrowLabel();

    const observer = new MutationObserver(() => {
        setZeroNavStockArrowLabel();
    });

    boxes.forEach((box) => {
        observer.observe(box, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class']
        });
    });

    window.__zeroNavArrowObserver = observer;
}

document.addEventListener('DOMContentLoaded', () => {
    delay(2000).then(watchZeroNavStockArrow);
    // QuoteMedia can render late; retry once the widget has had more time.
    delay(5000).then(watchZeroNavStockArrow);
});

/**
 * Component: QMod chart date-range buttons
 *
 * QuoteMedia renders the chart range buttons with an abbreviated visible label
 * (e.g. "1D", "YTD") inside an aria-hidden span, but sets a spelled-out
 * `aria-label` ("1 Day", "Year to Date"). Because the visible text isn't part of
 * the accessible name, that fails WCAG 2.5.3 (Label in Name). Prepend the visible
 * abbreviation to the accessible name so the visible label is contained within it
 * while keeping the spelled-out description for screen readers.
 */
function fixDateRangeButtonLabels() {
    document.querySelectorAll('button .qmod-btn-text').forEach((textEl) => {
        const btn = textEl.closest('button');
        if (!btn) {
            return;
        }

        const abbrev = textEl.textContent.trim();
        if (!abbrev) {
            return;
        }

        let full = (btn.getAttribute('aria-label') || '').trim();
        const prefix = abbrev + ', ';
        // Strip any prefix we previously added so this stays idempotent when
        // QuoteMedia re-applies its own aria-label on redraw.
        if (full.startsWith(prefix)) {
            full = full.slice(prefix.length);
        }

        const desired = (full && full !== abbrev) ? prefix + full : abbrev;
        if (btn.getAttribute('aria-label') !== desired) {
            btn.setAttribute('aria-label', desired);
        }
    });
}

/**
 * Component: QMod chart date-range buttons
 *
 * QuoteMedia rebinds the buttons' aria-labels on every redraw (range change,
 * tab switch), so keep a persistent observer that re-applies the fix. Writes are
 * idempotent, so the observer never loops on its own changes.
 */
function watchDateRangeButtonLabels() {
    if (window.__qmodRangeLabelObserver) {
        return;
    }

    fixDateRangeButtonLabels();

    const observer = new MutationObserver(() => {
        fixDateRangeButtonLabels();
    });

    observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-label']
    });

    window.__qmodRangeLabelObserver = observer;
}

document.addEventListener('DOMContentLoaded', () => {
    delay(2000).then(watchDateRangeButtonLabels);
    // QuoteMedia can render the chart late; retry once it has had more time.
    delay(5000).then(watchDateRangeButtonLabels);
});

/**
 * Page Type: QMod - Quote (Quote & Chart)
 *
 * QuoteMedia renders the chart credits ("© quotemedia") with a click handler
 * that navigates to quotemedia.com, plus an `aria-label` on the SVG `<text>`
 * node. After the link is removed the node is non-interactive, so `aria-label`
 * fails axe `aria-prohibited-attr`. Strip the click handler and the naming
 * ARIA attrs whenever the credits node is present (initial render and redraw).
 */
function disableQmodChartCreditsLink() {
    document.querySelectorAll('.page-type-qmod---quote .qmod-main-chart .highcharts-credits').forEach((credits) => {
        if (credits.hcEvents && credits.hcEvents.click) {
            credits.hcEvents.click.length = 0;
        }
        credits.style.cursor = 'default';
        // Only mutate when needed so the attribute observer does not loop.
        if (credits.hasAttribute('aria-label')) {
            credits.removeAttribute('aria-label');
        }
        if (credits.getAttribute('aria-hidden') !== 'true') {
            credits.setAttribute('aria-hidden', 'true');
        }
    });
}

function watchQmodChartCreditsLink() {
    if (!document.body.classList.contains('page-type-qmod---quote') || window.__qmodChartCreditsLinkObserver) {
        return;
    }

    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || !target.closest) {
            return;
        }
        const credits = target.closest('.page-type-qmod---quote .qmod-main-chart .highcharts-credits');
        if (!credits) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
    }, true);

    disableQmodChartCreditsLink();

    const observer = new MutationObserver(() => {
        disableQmodChartCreditsLink();
    });

    const root = document.querySelector('.page-type-qmod---quote .detailed-quote') || document.body;
    observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-label', 'aria-hidden']
    });

    window.__qmodChartCreditsLinkObserver = observer;
}

/**
 * Page Type: QMod - Quote (Quote & Chart)
 *
 * Dropdowns, day-range toggles, and the volume checkbox sit above the chart
 * with no programmatic relationship to it. Group those controls, and point the
 * range buttons and volume checkbox at the chart region. QuoteMedia redraws
 * this markup, so re-apply whenever the chart tool changes.
 */
function linkQmodChartToolbarToChart() {
    if (!document.body.classList.contains('page-type-qmod---quote')) {
        return;
    }

    document.querySelectorAll('.detailed-quote .qmod-chart-btns').forEach((toolbar) => {
        const chartRegion = toolbar.querySelector('.qmod-main-chart');
        const dropdowns = toolbar.querySelector('.qmod-dd-wrap');
        const rangeBar = toolbar.querySelector('.qmod-chart-buttons-rs');
        if (!chartRegion || (!dropdowns && !rangeBar)) {
            return;
        }

        if (chartRegion.getAttribute('role') !== 'region') {
            chartRegion.setAttribute('role', 'region');
        }
        if (!chartRegion.id) {
            const index = Array.prototype.indexOf.call(document.querySelectorAll('.qmod-main-chart'), chartRegion) + 1;
            chartRegion.id = 'qmod-chart-region-' + index;
        }

        let group = toolbar.querySelector(':scope > .qmod-chart-options-group');
        if (!group) {
            group = document.createElement('div');
            group.className = 'qmod-chart-options-group';
            const anchor = dropdowns || rangeBar;
            anchor.parentNode.insertBefore(group, anchor);
        }
        if (group.getAttribute('role') !== 'group') {
            group.setAttribute('role', 'group');
        }
        if (group.getAttribute('aria-label') !== 'Options for chart below') {
            group.setAttribute('aria-label', 'Options for chart below');
        }
        [dropdowns, rangeBar].forEach((controlRow) => {
            if (controlRow && controlRow.parentNode !== group) {
                group.appendChild(controlRow);
            }
        });

        const chartId = chartRegion.id;
        toolbar.querySelectorAll('.qmod-chart-buttons-rs button.qmod-group-btn, .qmod-volume-toggle input[type="checkbox"]').forEach((control) => {
            if (control.getAttribute('aria-controls') !== chartId) {
                control.setAttribute('aria-controls', chartId);
            }
        });
    });
}

function watchQmodChartToolbar() {
    if (!document.body.classList.contains('page-type-qmod---quote') || window.__qmodChartToolbarObserver) {
        return;
    }

    linkQmodChartToolbarToChart();

    const observer = new MutationObserver(() => {
        linkQmodChartToolbarToChart();
    });

    const root = document.querySelector('.page-type-qmod---quote .detailed-quote') || document.body;
    observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-controls']
    });

    window.__qmodChartToolbarObserver = observer;
}

/**
 * Page Type: QMod - Dividends
 * 
 * Wraps the pagination in a nav element with class "pagination-wrapper" and 
 * sets appropriate aria-labels for the nav and pagination buttons.
 */
function enhancePaginationAccessibility() {
    let pagination = document.querySelector('#DataTables_Table_0_paginate');
    if (!pagination) {
        pagination = document.querySelector('#DataTables_Table_1_paginate');
    }
    
    if (pagination) {
        // Track current page to detect actual page changes
        let currentPage = null;
        let isScrolling = false;
        
        // Function to get current page number
        function getCurrentPage() {
            const currentButton = pagination.querySelector('.paginate_button.current');
            return currentButton ? currentButton.textContent.trim() : null;
        }
        
        // Initialize current page
        currentPage = getCurrentPage();
        
        // Function to scroll to top of the dividends table and set focus
        function scrollToTableTop() {
            if (isScrolling) return;
            isScrolling = true;
            
            const newPage = getCurrentPage();
            
            // Only proceed if page actually changed
            if (newPage !== currentPage) {
                currentPage = newPage;
                
                const dividendsTable = document.querySelector('.dividends-data');
                if (dividendsTable) {
                    dividendsTable.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                    
                    // Set focus to the "Dividend History" title for context
                    setTimeout(() => {
                        try {
                            // Find the "Dividend History" heading
                            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
                            if (dividendHistoryHeading) {
                                // Make the heading focusable and set focus
                                dividendHistoryHeading.setAttribute('tabindex', '-1');
                                dividendHistoryHeading.focus();
                            }
                        } finally {
                            isScrolling = false;
                        }
                    }, 300); // Delay to ensure smooth scroll completes and table content is updated
                } else {
                    isScrolling = false;
                }
            } else {
                isScrolling = false;
            }
        }

        // Add accessible names to both dividend tables
        function addTableAccessibleName() {
            // Handle Upcoming Dividends table (DataTables_Table_0)
            const upcomingTable = document.querySelector('#DataTables_Table_0');
            const upcomingHeading = document.querySelector('.qmod-upcoming-dividends .qmod-heading');
            
            if (upcomingTable && upcomingHeading && !upcomingTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'upcoming-dividends-heading';
                upcomingHeading.setAttribute('id', headingId);
                upcomingTable.setAttribute('aria-labelledby', headingId);
            }
            
            // Handle Dividend History table (DataTables_Table_1)
            const historyTable = document.querySelector('#DataTables_Table_1');
            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
            
            if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'dividend-history-heading';
                dividendHistoryHeading.setAttribute('id', headingId);
                historyTable.setAttribute('aria-labelledby', headingId);
            }
        }

        // Create a nav element with the class "pagination-wrapper" and aria-label "Pagination"
        const navWrapper = document.createElement('nav');
        navWrapper.classList.add('pagination-wrapper');
        navWrapper.setAttribute('aria-label', 'Pagination');

        // Wrap the pagination element with the nav element
        pagination.parentNode.insertBefore(navWrapper, pagination);
        navWrapper.appendChild(pagination);

        // Function to set aria-labels for the next and previous buttons
        function setAriaLabels() {
            const prevButton = pagination.querySelector('.previous');
            const nextButton = pagination.querySelector('.next');
            
            if (prevButton) {
                prevButton.setAttribute('aria-label', 'Previous Page');
                // Fixes aria-prohibited-attr violation
                prevButton.setAttribute('role', 'link');
            }
            
            if (nextButton) {
                nextButton.setAttribute('aria-label', 'Next Page');
                // Fixes aria-prohibited-attr violation
                nextButton.setAttribute('role', 'link');
            }

            // Set aria-current="page" for the active page
            const pageButtons = pagination.querySelectorAll('.paginate_button');
            pageButtons.forEach(button => {
                if (button.classList.contains('current')) {
                    button.setAttribute('aria-current', 'page');
                } else {
                    button.removeAttribute('aria-current');
                }
            
                if (!button.classList.contains('previous') && !button.classList.contains('next')) {
                    const blabel = parseInt(button.getAttribute('data-dt-idx'), 10) + 1;
                    button.setAttribute('aria-label', 'Page ' + blabel);
                }
            
                // Fix aria-role attribute (convert to proper role attribute)
                const ariaRoleValue = button.getAttribute('aria-role');
                if (ariaRoleValue) {
                    button.removeAttribute('aria-role');
                    button.setAttribute('role', ariaRoleValue);
                }
                
            });
            
            // Add screen reader text to ellipses instead of prohibited aria-label
            const ellipses = pagination.querySelectorAll('.ellipsis');
            ellipses.forEach(ellipsis => {
                // Check if visually-hidden span already exists to avoid duplicates
                if (!ellipsis.querySelector('.visually-hidden')) {
                    const srOnlySpan = document.createElement('span');
                    srOnlySpan.className = 'visually-hidden';
                    srOnlySpan.textContent = 'Ellipsis indicating non-visible pages';
                    ellipsis.appendChild(srOnlySpan);
                }
            });
        };

        // Add accessible names to both dividend tables
        function addTableAccessibleName() {
            // Handle Upcoming Dividends table (DataTables_Table_0)
            const upcomingTable = document.querySelector('#DataTables_Table_0');
            const upcomingHeading = document.querySelector('.qmod-upcoming-dividends .qmod-heading');
            
            if (upcomingTable && upcomingHeading && !upcomingTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'upcoming-dividends-heading';
                upcomingHeading.setAttribute('id', headingId);
                upcomingTable.setAttribute('aria-labelledby', headingId);
            }
            
            // Handle Dividend History table (DataTables_Table_1)
            const historyTable = document.querySelector('#DataTables_Table_1');
            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
            
            if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'dividend-history-heading';
                dividendHistoryHeading.setAttribute('id', headingId);
                historyTable.setAttribute('aria-labelledby', headingId);
            }
        }

        // Apply aria-labels initially
        setAriaLabels();
        
        // Add table accessibility initially
        addTableAccessibleName();

        // Simple MutationObserver for aria labels and table accessibility only
        const observer = new MutationObserver(() => {
            setAriaLabels();
            addTableAccessibleName(); // Ensure table accessibility is maintained
        });
        observer.observe(pagination, { childList: true, subtree: true });

        // Use event delegation to handle pagination clicks and keyboard events
        pagination.addEventListener('click', (e) => {
            const button = e.target.closest('.paginate_button:not(.disabled)');
            if (button && !button.classList.contains('current')) {
                setTimeout(scrollToTableTop, 200);
            }
        });

        // Also listen for keyboard events (Enter/Space)
        pagination.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                const button = e.target.closest('.paginate_button:not(.disabled)');
                if (button && !button.classList.contains('current')) {
                    setTimeout(scrollToTableTop, 200);
                }
            }
        });

    }
}

// Call the function with a delay after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    delay(2000).then(() => {
        enhancePaginationAccessibility();
    });
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds meaningful aria-label to empty table headers.
 * 
 * @since QMod v1.69.0
 */
function fixEmptyTableHeaders() {
    return new Promise((resolve) => {
        const emptyHeaders = document.querySelectorAll('th.qmod-control.control.sorting_disabled[aria-label=""]');
        emptyHeaders.forEach(header => {
            header.setAttribute('aria-label', 'Empty header');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    fixEmptyTableHeaders();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds a valid role to .qmod-a11y-toggle-wrap.qmod-volume-toggle elements.
 * 
 * @since QMod v1.69.0
 */
function addTabIndexToVolumeToggle() {
    return new Promise((resolve) => {
        const toggleWrapElements = document.querySelectorAll('.qmod-a11y-toggle-wrap.qmod-volume-toggle');
        toggleWrapElements.forEach(element => {
            element.setAttribute('tabindex', '-1');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    addTabIndexToVolumeToggle();
});